import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import {
  createProblem,
  problemPatternIncidentsQuery,
  problemsPatternsQuery,
  type PatternRow,
} from '../api/problems'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * `Problems::candidates` - tiparele, la /app/problems/candidates.
 *
 * Ecranul asta e capatul CMDB-ului. Tot ce s-a construit inainte - elementele,
 * legaturile lor, mediul, criticitatea - se aduna aici intr-o singura propozitie
 * pe fiecare rand: „asta s-a stricat de N ori, si nimeni nu s-a uitat de ce".
 *
 * JQUERY NU RULEAZA AICI. `selectpicker` de pe filtrul de tipuri, randul care se
 * desface sub fiecare tipar, si fereastra de deschidere a unei probleme erau
 * toate purtate de `public/js/problems.js`. v4 incarca foile portalului si NICIUNUL
 * din scripturile lui, deci fiecare din ele e facut aici: `SelectPicker`,
 * `Modal`, si o cerere proprie pentru incidentele randului.
 */

const STYLES = ['css/adm.css']

export function ProblemPatternsPage() {
  const shell = useQuery(shellQuery(moduleOf('/problems')))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const [days, setDays] = useState(90)
  const [min, setMin] = useState(3)
  const [types, setTypes] = useState<number[]>([])
  const page = useQuery(problemsPatternsQuery(days, min, types))

  const [open, setOpen] = useState<PatternRow | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data

  return (
    <Content>
      <div id="content">
        <div className="adm-card" style={{ marginBottom: 16 }}>
          <p style={{ margin: '0 0 6px' }}>
            Configuration items that have collected <strong>at least {data.options.min}</strong> incidents in the last{' '}
            <strong>{data.options.days}</strong> days, and on which <strong>no problem has been opened yet</strong>.
          </p>
          <p className="text-muted small" style={{ margin: 0 }}>
            {/* Se spune de unde vin numerele. Un ecran care arata „14" fara sa
                spuna ce a numarat produce fie incredere oarba, fie neincredere -
                si amandoua sunt mai rele decat un rand de explicatie. */}
            Counted are the tickets &mdash; open and closed alike &mdash; that touch the item, either because it was
            picked on the ticket itself, or through an endpoint under it. A ticket that arrives by both routes is
            counted once.
          </p>
        </div>

        <div className="form-inline" style={{ marginBottom: 16 }}>
          <div className="form-group" style={{ marginRight: 10 }}>
            <label style={{ marginRight: 5 }}>Window</label>
            <select className="form-control input-sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {data.windows.map((option) => (
                <option key={option} value={option}>
                  {option} days
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginRight: 10 }}>
            <label style={{ marginRight: 5 }}>From</label>
            <select className="form-control input-sm" value={min} onChange={(e) => setMin(Number(e.target.value))}>
              {data.minimums.map((option) => (
                <option key={option} value={option}>
                  {option} incidents
                </option>
              ))}
            </select>
          </div>
          {data.types.length > 0 && (
            <div className="form-group" style={{ marginRight: 10 }}>
              <label style={{ marginRight: 5 }}>Types</label>
              <SelectPicker
                id="pattern-types"
                className="form-control input-sm"
                title="All types"
                multiple
                actionsBox
                selectedTextFormat="count > 2"
                options={data.types.map((type) => ({ value: String(type.ID), label: type.type_name }))}
                value={types.map(String)}
                onChange={(chosen) => setTypes(chosen.map(Number))}
              />
            </div>
          )}
        </div>

        {types.length === 0 && (
          <p className="text-muted small" style={{ marginTop: -8, marginBottom: 16 }}>
            {/* Platforma n-are un semn care sa spuna care tip de tichet e
                incident si care e cerere - se vede si in rapoartele vechi, unde
                tipurile sunt scrise cu numarul lor. Deci se numara toate, si se
                SPUNE ca se numara toate. A ghici aici ar fi insemnat sa inventam
                o clasificare si sa raspundem din ea cu incredere. */}
            <i className="fas fa-info-circle"></i> <strong>Every</strong> ticket type is counted. The platform holds no
            flag saying which type is an incident and which is a request &mdash; if only certain types are incidents for
            you, pick them above.
          </p>
        )}

        {data.candidates.length === 0 ? (
          <div className="adm-card">
            <p style={{ margin: 0 }}>
              No item has collected {data.options.min} incidents in the last {data.options.days} days.
            </p>
            <p className="text-muted small" style={{ margin: '6px 0 0' }}>
              {/* Gol nu inseamna „totul e bine". Cel mai probabil inseamna ca
                  tichetele nu spun pe ce element s-au deschis - si aia e o
                  constatare despre completare, nu despre stabilitate. */}
              That can mean two very different things: either nothing repeats, or the tickets do not say what they were
              opened on. The second one shows up in <a href="/app/cmdb/health">CMDB health</a>.
            </p>
          </div>
        ) : (
          <>
            <table className="table table-hover adm-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ width: 110 }} className="center">
                    Incidents
                  </th>
                  <th style={{ width: 150 }}>First</th>
                  <th style={{ width: 150 }}>Last</th>
                  <th style={{ width: 120 }} className="center">
                    Rate
                  </th>
                  <th style={{ width: 230 }} className="center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.candidates.map((row) => (
                  <RowPair
                    key={row.ciId}
                    row={row}
                    days={days}
                    types={types}
                    open={expanded === row.ciId}
                    onToggle={() => setExpanded(expanded === row.ciId ? null : row.ciId)}
                    onOpenProblem={() => setOpen(row)}
                  />
                ))}
              </tbody>
            </table>

            {data.candidates.length >= data.limit && (
              <p className="text-warning small">
                {/* Plafonul se spune, nu se lasa lista sa se citeasca drept
                    „atatea sunt". */}
                <i className="fas fa-exclamation-triangle"></i> Showing the first {data.limit}, the ones with the most
                incidents. There may be more.
              </p>
            )}
          </>
        )}
      </div>

      <NewProblemModal row={open} days={days} types={types} onClose={() => setOpen(null)} />
    </Content>
  )
}

function RowPair({
  row,
  days,
  types,
  open,
  onToggle,
  onOpenProblem,
}: {
  row: PatternRow
  days: number
  types: number[]
  open: boolean
  onToggle(): void
  onOpenProblem(): void
}) {
  return (
    <>
      <tr>
        <td>
          <a href={`/app/cmdb/view/${row.ciId}`} target="_blank" rel="noopener">
            {row.ciName}
          </a>{' '}
          {row.environment === 1 && <span className="adm-pill adm-pill-warn">production</span>}{' '}
          {row.criticality !== null && row.criticality >= 4 && <span className="adm-pill adm-pill-bad">critical</span>}
        </td>
        <td className="center">
          <strong style={{ fontSize: 15 }}>{row.incidents}</strong>
        </td>
        <td className="text-muted">{row.firstSeen}</td>
        <td>{row.lastSeen}</td>
        <td className="center">
          {row.every === null ? (
            <span className="text-muted">&mdash;</span>
          ) : (
            <span className="text-muted">every {row.every} days</span>
          )}
        </td>
        <td className="center">
          <button className="btn btn-xs btn-default show-pattern-incidents" onClick={onToggle}>
            <i className="fas fa-list"></i> View the incidents
          </button>{' '}
          <button className="btn btn-xs btn-primary open-problem" onClick={onOpenProblem}>
            <i className="fas fa-plus"></i> Open a problem
          </button>
        </td>
      </tr>
      {open && (
        <tr className="pattern-incidents" id={`pattern-${row.ciId}`}>
          <td colSpan={6} style={{ background: '#fafafa' }}>
            <PatternIncidents ciId={row.ciId} days={days} types={types} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * Incidentele din spatele unui tipar, deschise sub randul lui.
 *
 * Se cer inainte ca cineva sa faca o problema: nimeni n-ar trebui sa afirme
 * „astea paisprezece sunt acelasi lucru" fara sa le fi citit subiectele.
 */
function PatternIncidents({ ciId, days, types }: { ciId: number; days: number; types: number[] }) {
  const panel = useQuery(problemPatternIncidentsQuery(ciId, days, types))

  if (panel.error) {
    return <span className="text-muted">The incidents could not be read.</span>
  }
  if (!panel.data) return <Spinner />
  if (panel.data.incidents.length === 0) {
    return (
      <p className="text-muted" style={{ margin: '8px 0' }}>
        No incident in the last {panel.data.days} days.
      </p>
    )
  }

  return (
    <table className="table table-condensed" style={{ margin: '8px 0 0', background: 'transparent' }}>
      <tbody>
        {panel.data.incidents.map((row) => (
          <tr key={row.id}>
            <td style={{ width: 80 }}>
              <a href={`/app/easeedesk/ticket/${row.id}`} target="_blank" rel="noopener">
                #{row.id}
              </a>
            </td>
            <td>{row.subject}</td>
            <td style={{ width: 130 }} className="text-muted">
              {row.created}
            </td>
            <td style={{ width: 90 }}>
              {/* Se spune care e inchis. Un tipar facut numai din incidente
                  inchise arata altfel decat unul in care ceva inca arde - al
                  doilea nu mai e o problema de investigat luni, e una de acum. */}
              {row.closed ? (
                <span className="text-muted small">closed</span>
              ) : (
                <span className="adm-pill adm-pill-warn">open</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Fereastra de deschidere a unei probleme dintr-un tipar.
 *
 * Ocolirea se cere AICI si nu abia pe fisa: de obicei se stie din prima ora -
 * „repornesti serviciul si merge" - cu mult inainte sa se afle cauza. Ceruta mai
 * tarziu, ar fi ramas nescrisa, iar urmatorul om ar fi cautat-o de la zero.
 */
function NewProblemModal({
  row,
  days,
  types,
  onClose,
}: {
  row: PatternRow | null
  days: number
  types: number[]
  onClose(): void
}) {
  const navigate = useNavigate()
  const [form, setForm] = useState({ title: '', description: '', workaround: '' })
  const [busy, setBusy] = useState(false)
  const [seen, setSeen] = useState(0)

  /* Titlul se PROPUNE, nu se impune: cine deschide problema stie ce se intampla
     mai bine decat noi, dar un camp gol la ora 18 se completeaza cu „test" sau
     nu se completeaza deloc. */
  if (row !== null && seen !== row.ciId) {
    setSeen(row.ciId)
    setForm({ title: `${row.ciName} — repeated incidents`, description: '', workaround: '' })
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (row === null || form.title.trim() === '') return
    setBusy(true)
    void createProblem({
      ...form,
      ci_id: row.ciId,
      from_pattern: 1,
      days,
      'types[]': types,
    })
      .then((result) => {
        setBusy(false)
        if (result.status !== 'OK') {
          toast(result.message ?? 'It did not work.', 'Problems', 'error')
          return
        }
        onClose()
        void navigate({ to: '/problems/view/$id', params: { id: String(result.id ?? 0) } })
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError ? error.message : 'It did not work.', 'Problems', 'error')
      })
  }

  return (
    <Modal id="newProblemModal" open={row !== null} onClose={onClose} title="Open a problem">
      <form id="new-problem-form" onSubmit={submit}>
        <p className="text-muted problem-context" style={{ marginBottom: 12 }}>
          {row !== null && `${row.incidents} incidents on ${row.ciName} will be linked to this problem.`}
        </p>

        <div className="form-group">
          <label className="control-label">
            Title <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control input-sm"
            required
            maxLength={255}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="control-label">What is known so far</label>
          <textarea
            className="form-control input-sm"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="control-label">How to work around it, until it is fixed</label>
          <textarea
            className="form-control input-sm"
            rows={2}
            placeholder="What someone hitting this at 3am should do"
            value={form.workaround}
            onChange={(e) => setForm({ ...form, workaround: e.target.value })}
          />
          <span className="help-block small">Shown on every incident linked to this problem.</span>
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Open
          </button>
        </div>
      </form>
    </Modal>
  )
}
